<template>
  <BarChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  >
    <template #top-right-slot>
      <v-switch
        v-if="hasThemeFilter"
        v-model="showAllThemes"
        :label="$t('mixins.sentimentModel.showAllThemes')"
        dense
        hide-details
      />
    </template>
  </BarChart>
</template>

<script>
import BarChart from '@/components/cards/models/BarChart';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import { POST_FIELDS, OPERATION, ENTITIES } from '@/services/charts-service';
import { FILTER_NAMES } from '@/store/modules/cards/constants';
import EventBus from '@/utils/event-bus';
import { mapActions } from 'vuex';
import highchartsBasics from '@/mixins/highcharts-basics';

export default {
  name: 'BaseShareSentiment',
  components: {
    BarChart,
  },
  mixins: [
    sentimentModel,
    cardsModel,
    highchartsBasics,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    field: {
      type: String,
      default: null,
    },
    group: {
      type: String,
      required: true,
    },
    entity: {
      type: String,
      default: ENTITIES.POSTS,
    },
    label: {
      type: String,
      default: null,
    },
    hideNull: {
      type: Boolean,
      default: false,
    },
    polarity: {
      type: Boolean,
      default: false,
    },
    stackLabels: {
      type: [Boolean, String],
      default: true,
    },
    filterName: {
      type: String,
      default: null,
    },
    isExpandedCard: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawData: [],
      pointsParser: 'points',
      filter: {},
      showAllThemes: false,
      cardOptions: {
        yAxis: {
          stackLabels: {
            enabled: Boolean(this.stackLabels),
            ...(this.stackLabels === 'percentage'
              ? { formatter: (component => {
                return function() {
                  return `${component.sumSeries
                    ? ((this.total / component.sumSeries) * 100).toFixed(1)
                    : 0}%`;
                };
              })(this) }
              : null),
          },
        },
        plotOptions: {
          series: {
            stacking: 'normal',
          },
        },
      },
    };
  },
  computed: {
    hasThemeFilter() {
      return this.group === POST_FIELDS.THEME &&
        this.filterName === FILTER_NAMES.THEME &&
        this.polarity &&
        !this.field &&
        !this.label &&
        Boolean(this.filter.themes?.value?.length);
    },
    dataForSeries() {
      return this.group === POST_FIELDS.THEME && !this.showAllThemes
        ? this.removeInvalidThemes({ points: this.rawData })
        : this.rawData;
    },
    series() {
      const filteredValues = this.showAllThemes ? null : this.filter[this.filterName]?.value;
      const { pointsParser } = this;
      const data = [...this.dataForSeries];

      if (this.label) {
        data.forEach(p => {
          p.y = this.label;
          return p;
        });
      }
      const filteredData = filteredValues?.length
        ? data.filter(point => filteredValues.includes(point.x) || filteredValues.includes(point.y))
        : data;
      const rawSeries = this.normalizeSentimentResponseFromChartService(filteredData, pointsParser, this.hideNull);

      if (!this.isExpandedCard) {
        return this.normalizeSeries(rawSeries, this.showAllThemes);
      }

      return rawSeries;
    },
    sumSeries() {
      return this.series.reduce((sum, serie) => {
        return sum + serie.data.reduce((sum, point) => sum + point.y, 0);
      }, 0);
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }
      const grouping = [this.group];
      if (this.polarity && !this.field) {
        grouping.push(POST_FIELDS.POLARITY);
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getAggregatedData',
        points_parser: this.pointsParser,
        entity: this.entity,
        ignoreThemeFilter: this.group === POST_FIELDS.THEME,
        params: {
          grouping,
          operators: this.operation,
          ...(this.field ? { fields: [this.field] } : null),
          datePortion: this.getDatePortion(options),
        },
      })
        .then((response) => {
          this.rawData = response?.points || [];

          this.requestStatus = 'success';
          if (this.$attrs.filter) {
            this.filter = this.$attrs.filter;
          }

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }
        })
        .catch((err) => {
          console.error(err);
          this.requestStatus = 'error';
        });
    },
    normalizeSeries(series, showAllItems = false) {
      // when series comes with not classified sentiment
      if (series.length === 1) {
        series[0].data.sort((first, second) => first.x - second.x);

        return [{
          ...series[0],
          data: showAllItems ? series[0].data : series[0].data?.slice(0, this.maxItemsToBeDisplayed),
        }];
      }

      // when series comes with sentiments
      return series.map(serie => {
        const newData = showAllItems
          ? serie.data
          : serie.data.filter(each => each.x < this.maxItemsToBeDisplayed);

        return {
          ...serie,
          data: newData,
        };
      });
    },
    removeInvalidThemes(response) {
      if (this.polarity) {
        return response?.points?.filter(({ y }) => this.activeDashboardFilters.themes.includes(y)) || [];
      }
      return response?.points?.filter(({ x }) => this.activeDashboardFilters.themes.includes(x)) || [];
    },
  },
};
</script>
